import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  FormSelect,
  FormText,
  PaginationNavButton,
  PaginationPageButton,
  ShipmentBulkUploadAccordionRow,
  ShipmentBulkUploadActionBar,
} from '..'
import {
  buildSelectionChips,
  mergeSelection,
  toggleSelection,
} from '../../utils/shipmentSelection'
import { useAppSelector } from '../../store/hooks'
import {
  ShipmentGroup,
  ShipmentGroupErrors,
  ShipmentPieceRow,
} from '../../interfaces/Import/ShipmentGroupInterface'
import { DestinationInterface } from '../../interfaces/Account/DestinationInterface'
import { ServiceDTO } from '../../interfaces/Services/Services'

const pageSizes = [5, 10, 20, 50]

export const ShipmentBulkUploadAccordionList: FC<{
  groups: ShipmentGroup[]
  errors: ShipmentGroupErrors[]
  identificationTypes: any[]
  typeShipment: { value: string; name: string }[]
  /** Catálogo COMPLETO. Para resolver/mostrar una tienda ya asignada. */
  businessUnits: any[]
  /** Tiendas destino SELECCIONABLES (las que el contrato permite). */
  destinationOptions?: any[]
  cities: any[]
  citiesByBu?: any[]
  pieceCategories: any[]
  isCOD: boolean
  serviceOptions?: ServiceDTO[]
  onChangeGroup: (index: number, patch: Partial<ShipmentGroup>) => void
  onChangePiece: (
    groupIndex: number,
    pieceIndex: number,
    patch: Partial<ShipmentPieceRow>
  ) => void
  onAddPiece: (groupIndex: number) => void
  onRemovePiece: (groupIndex: number, pieceIndex: number) => void
  validatePiece: (
    p: ShipmentPieceRow,
    serviceId?: number
  ) => Partial<Record<keyof ShipmentPieceRow, string>>
  onRequestMerge?: (index: number) => void
  phonePrefixList?: { value: string; text: string }[]
  favoriteDestinations?: DestinationInterface[]
  favsByRif?: Map<string, DestinationInterface>
  /** Aplica un mismo patch a varias guías. Sin este handler la lista no muestra
   *  checkboxes ni barra de acciones (el flujo de corrección no los necesita). */
  onApplyBulk?: (indices: number[], patch: Partial<ShipmentGroup>) => void
}> = ({
  groups,
  errors,
  identificationTypes,
  typeShipment,
  businessUnits,
  destinationOptions,
  cities,
  citiesByBu = [],
  pieceCategories,
  isCOD,
  serviceOptions = [],
  onChangeGroup,
  onChangePiece,
  onAddPiece,
  onRemovePiece,
  validatePiece,
  onRequestMerge,
  phonePrefixList = [],
  favoriteDestinations = [],
  favsByRif,
  onApplyBulk,
}) => {
  // Mismo criterio que la fila y la barra de acciones: las oficinas origen se leen
  // del store en vez de prop-drillearlas desde el módulo.
  const user = useAppSelector((s) => s.user)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(10)
  const [query, setQuery] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  // Selección múltiple por id (no por índice: el orden de la lista se recalcula
  // sola cuando cambian los errores, y un índice guardado apuntaría a otra guía).
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [activeChip, setActiveChip] = useState<string | null>(null)

  const normalized = (s: string) => (s || '').toString().toLowerCase()

  const matchesQuery = useCallback((g: ShipmentGroup, q: string) => {
    if (!q) return true
    const fields = [
      g.shipmentHeaderReference,
      g.consigneeName,
      g.consigneeDocNumber,
      g.consigneePhone,
      g.destinationAddress,
      g.storeCode,
      g.destinationCity,
    ]
    return fields.some((v) => normalized(String(v || '')).includes(q))
  }, [])

  const computeOrderedIds = useCallback((): string[] => {
    const pairs = groups.map((g, i) => ({ g, i }))
    const rank = (pair: { g: ShipmentGroup; i: number }) => {
      if (pair.g.coordWarning) return 1
      if (
        Object.keys(errors[pair.i] || {}).length > 0 ||
        pair.g.isDuplicateInDB
      )
        return 0
      return 2
    }
    return [...pairs]
      .sort((a, b) => {
        const ra = rank(a)
        const rb = rank(b)
        if (ra !== rb) return ra - rb
        return a.g.shipmentHeaderReference.localeCompare(b.g.shipmentHeaderReference, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      })
      .map((p) => p.g.id)
  }, [groups, errors])

  const [orderedIds, setOrderedIds] = useState<string[]>(computeOrderedIds)

  useEffect(() => {
    setOrderedIds((prev) => {
      const live = new Set(groups.map((g) => g.id))
      const kept = prev.filter((id) => live.has(id))
      const known = new Set(kept)
      const added = groups.filter((g) => !known.has(g.id)).map((g) => g.id)
      if (added.length === 0 && kept.length === prev.length) return prev
      return [...kept, ...added]
    })
    setOpenId((prev) =>
      prev && groups.some((g) => g.id === prev) ? prev : null
    )
  }, [groups])

  useEffect(() => {
    if (openId !== null) return
    const next = computeOrderedIds()
    setOrderedIds((prev) =>
      prev.length === next.length && prev.every((id, i) => id === next[i])
        ? prev
        : next
    )
  }, [computeOrderedIds, openId])

  const idToIndex = useMemo(() => {
    const m = new Map<string, number>()
    groups.forEach((g, i) => m.set(g.id, i))
    return m
  }, [groups])

  const filteredRows = useMemo(() => {
    const q = normalized(query)
    const rows: { g: ShipmentGroup; i: number }[] = []
    for (const id of orderedIds) {
      const i = idToIndex.get(id)
      if (i == null) continue
      const g = groups[i]
      if (matchesQuery(g, q)) rows.push({ g, i })
    }
    return rows
  }, [orderedIds, idToIndex, groups, query, matchesQuery])

  const total = filteredRows.length
  const totalPages = Math.max(1, Math.ceil(total / size))
  const safePage = Math.min(page, totalPages - 1)
  const start = safePage * size
  const end = Math.min(start + size, total)
  const pageRows = useMemo(
    () => filteredRows.slice(start, end),
    [filteredRows, start, end]
  )

  const commitReorder = useCallback(
    (groupId: string) => {
      const newIds = computeOrderedIds()
      setOrderedIds(newIds)
      const q = normalized(query)
      const visible = newIds.filter((id) => {
        const i = idToIndex.get(id)
        return i != null && matchesQuery(groups[i], q)
      })
      const pos = visible.indexOf(groupId)
      if (pos < 0) return
      setPage(Math.floor(pos / size))
      setTimeout(() => {
        document
          .getElementById(`bulk-row-${groupId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 60)
    },
    [computeOrderedIds, query, idToIndex, groups, size, matchesQuery]
  )

  const handleToggle = useCallback(
    (groupId: string) => {
      commitReorder(groupId)
      setOpenId((prev) => (prev === groupId ? null : groupId))
    },
    [commitReorder]
  )

  // ─── Selección múltiple ───────────────────────────────────────────────────
  // Requiere DOS cosas: que el contenedor sepa aplicar en lote, y que el contrato
  // tenga algo que elegir. Con una sola oficina origen y un solo servicio el
  // front ya los auto-asigna, así que mostrar checkboxes dejaría al operador
  // seleccionando guías para una barra que no puede ofrecerle ningún campo.
  const hasBulkFields =
    (user.originBusinessUnits?.length ?? 0) > 1 || serviceOptions.length > 1
  const selectionEnabled = !!onApplyBulk && hasBulkFields

  // Una guía fusionada o eliminada deja un id colgado en la selección: se poda
  // contra los grupos vivos para no aplicarle un patch a algo que ya no está.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.length === 0) return prev
      const live = new Set(groups.map((g) => g.id))
      const kept = prev.filter((id) => live.has(id))
      return kept.length === prev.length ? prev : kept
    })
  }, [groups])

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  // Los atajos y el "marcar todas" operan sobre lo FILTRADO, no sobre las 403:
  // si el operador buscó "MBO", marcar todas debe marcar lo que está viendo.
  const filteredIds = useMemo(
    () => filteredRows.map((r) => r.g.id),
    [filteredRows]
  )

  const chips = useMemo(
    () =>
      buildSelectionChips(
        filteredRows.map(({ g, i }) => ({
          id: g.id,
          destinationCity: g.destinationCity,
          hasError:
            !!g.coordWarning ||
            Object.keys(errors[i] || {}).length > 0 ||
            !!g.isDuplicateInDB,
        }))
      ),
    [filteredRows, errors]
  )

  const selectedInFilter = filteredIds.filter((id) => selectedSet.has(id)).length
  const allFilteredSelected =
    filteredIds.length > 0 && selectedInFilter === filteredIds.length

  const clearSelection = useCallback(() => {
    setSelectedIds([])
    setActiveChip(null)
  }, [])

  const handleToggleAll = useCallback(() => {
    setSelectedIds((prev) =>
      mergeSelection(prev, filteredIds, !allFilteredSelected, (s) => s)
    )
    setActiveChip(null)
  }, [filteredIds, allFilteredSelected])

  const handleToggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => toggleSelection(prev, id, (s) => s))
    // Tocar una fila a mano deja de ser "el chip": el resaltado se apaga para no
    // afirmar que la selección sigue siendo exactamente ese subconjunto.
    setActiveChip(null)
  }, [])

  const handleChipClick = useCallback(
    (key: string, ids: string[]) => {
      if (activeChip === key) {
        clearSelection()
        return
      }
      // El chip REEMPLAZA la selección (no suma): es un atajo, y sumar en
      // silencio sobre lo que ya había hace que "Aplicar a N" toque guías que el
      // operador no tiene a la vista.
      setSelectedIds(ids)
      setActiveChip(key)
    },
    [activeChip, clearSelection]
  )

  const handleApply = useCallback(
    (patch: Partial<ShipmentGroup>) => {
      if (!onApplyBulk) return
      const indices = selectedIds
        .map((id) => idToIndex.get(id))
        .filter((i): i is number => i != null)
      if (indices.length > 0) onApplyBulk(indices, patch)
    },
    [onApplyBulk, selectedIds, idToIndex]
  )

  const windowSize = totalPages > 5 ? 4 : totalPages
  let windowStart = safePage
  if (windowStart + windowSize > totalPages)
    windowStart = Math.max(0, totalPages - windowSize)
  const pagesWindow = Array.from(
    { length: windowSize },
    (_, i) => windowStart + i
  )

  return (
    <div className="w-full">
      <div className="flex items-end justify-between mb-3 gap-3">
        <FormSelect<number>
          id="shipment-v2-page-size"
          name="shipment-v2-page-size"
          label=""
          selected={size}
          options={pageSizes}
          optionString={(opt) => `${opt}/pág`}
          onSelectOption={(opt) => {
            setSize(opt)
            setPage(0)
          }}
          className="!h-8"
          containerClassname="w-40"
        />
        <div className="flex-1 max-w-md">
          <FormText
            id="shipment-v2-search"
            name="shipment-v2-search"
            label="Buscar"
            placeholder="Nº guía, destinatario…"
            value={query}
            onChange={(e: any) => {
              setQuery(e.target.value)
              setPage(0)
            }}
            className="!h-8"
          />
        </div>
      </div>

      {selectionEnabled && filteredRows.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
          <input
            type="checkbox"
            id="bulk-select-all"
            checked={allFilteredSelected}
            ref={(el) => {
              // Indeterminado = "hay algo marcado, pero no todo". Solo se puede
              // setear por DOM, no existe como atributo de React.
              if (el)
                el.indeterminate = selectedInFilter > 0 && !allFilteredSelected
            }}
            onChange={handleToggleAll}
            className="h-4 w-4 cursor-pointer rounded border-gray-300 text-main-500 focus:ring-main-500"
          />
          <label
            htmlFor="bulk-select-all"
            className="text-sm text-gray-700 cursor-pointer select-none"
          >
            {selectedInFilter > 0
              ? `${selectedInFilter} de ${filteredIds.length}`
              : `Marcar las ${filteredIds.length}`}
          </label>

          {chips.length > 0 && (
            <>
              <span className="text-gray-300" aria-hidden="true">
                |
              </span>
              <span className="text-xs text-gray-500">Marcar:</span>
              {chips.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => handleChipClick(c.key, c.ids)}
                  aria-pressed={activeChip === c.key}
                  className={`text-xs rounded-full px-3 py-1 border transition-colors ${
                    activeChip === c.key
                      ? 'bg-main-50 border-main-500 text-main-500'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-main-400 hover:text-main-500'
                  }`}
                >
                  {c.label}{' '}
                  <span className="font-semibold tabular-nums">{c.count}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}

      {pageRows.map(({ g, i }) => (
        <div key={g.id} id={`bulk-row-${g.id}`}>
          <ShipmentBulkUploadAccordionRow
            group={groups[i]}
            index={i}
            open={openId === g.id}
            onToggle={() => handleToggle(g.id)}
            errors={errors[i] || {}}
            identificationTypes={identificationTypes}
            typeShipment={typeShipment}
            businessUnits={businessUnits}
            destinationOptions={destinationOptions}
            cities={cities}
            citiesByBu={citiesByBu}
            pieceCategories={pieceCategories}
            isCOD={isCOD}
            serviceOptions={serviceOptions}
            onChangeGroup={(patch) => onChangeGroup(i, patch)}
            onChangePiece={(pIdx, patch) => onChangePiece(i, pIdx, patch)}
            onAddPiece={() => onAddPiece(i)}
            onRemovePiece={(pIdx) => onRemovePiece(i, pIdx)}
            validatePiece={validatePiece}
            onRequestMerge={
              onRequestMerge ? () => onRequestMerge(i) : undefined
            }
            phonePrefixList={phonePrefixList}
            favoriteDestinations={favoriteDestinations}
            favsByRif={favsByRif}
            selectable={selectionEnabled}
            selected={selectedSet.has(g.id)}
            onToggleSelected={() => handleToggleRow(g.id)}
          />
        </div>
      ))}

      <ShipmentBulkUploadActionBar
        count={selectedIds.length}
        serviceOptions={serviceOptions}
        onApply={handleApply}
        onClear={clearSelection}
      />

      <div className="flex items-center justify-between mt-3 text-sm">
        <div>
          Página {safePage + 1} de {totalPages}
        </div>
        <div className="flex items-center gap-2">
          <PaginationNavButton
            direction="prev"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safePage === 0}
          />
          {pagesWindow.map((p) => (
            <PaginationPageButton
              key={p}
              page={p + 1}
              active={p === safePage}
              onClick={() => setPage(p)}
            />
          ))}
          <PaginationNavButton
            direction="next"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={safePage >= totalPages - 1}
          />
        </div>
      </div>
    </div>
  )
}
